import { useState, useMemo, useCallback } from 'react';
import { clsx } from 'clsx';

import { ActionBar } from './ActionBar';
import { ContextStrip } from './ContextStrip';
import { HeaderRenderer } from './HeaderRenderer';
import { LinesSection } from './LinesSection';
import { FactBox, FactBoxInline } from './FactBox';
import { ContentLayout } from './ContentLayout';
import { LineFormModal } from './LineFormModal';
import { ConflictModal } from './ConflictModal';
import { SkeletonDocument } from './Skeletons';
import { CrossingSourceSelector } from './CrossingSourceSelector';
import { StaleDataBanner } from './StaleDataBanner';
import { CrossingClassMenu } from './CrossingClassMenu';
import { PendingLinesPanel } from './PendingLinesPanel';
import { DocumentTraceabilityTree } from './DocumentTraceabilityTree';
import { LineCloseModal } from './LineCloseModal';
import { CrossingGenerateModal } from './CrossingGenerateModal';
import { LineTabsRegion } from './LineTabsRegion';

import type {
  MasterDocumentProps,
  FieldDescriptor,
  FieldErrorMap,
  LineTabConfig,
  LineTabHelpers,
} from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';
import { inlineActionButtonDanger, inlineActionButtonNeutral } from './inlineActions';

// ─── Helpers ──────────────────────────────────────────────

/**
 * Clave reservada de la pestaña de trazabilidad del cruce.
 *
 * La trazabilidad ya era una pestaña antes de que existieran las pestañas
 * declarativas, con su propio estado (`crossing.activeTab`) y su unión cerrada
 * `'lines' | 'traceability'` en un callback público. En vez de ensanchar ese tipo
 * —lo que rompería la compilación de cualquier host que implemente el handler—,
 * se la anexa a la barra como una pestaña sintética y esta clave hace de puente:
 * al seleccionarla se traduce a `crossing.onTabChange('traceability')`.
 */
const TRACEABILITY_TAB_KEY = '__traceability';

function groupFieldsBySection(fields: FieldDescriptor[]): Record<string, FieldDescriptor[]> {
  const map: Record<string, FieldDescriptor[]> = {};
  for (const f of fields) {
    (map[f.section] ??= []).push(f);
  }
  return map;
}

// ─── MasterDocument ───────────────────────────────────────

/**
 * MasterDocument — componente de presentación que compone el documento maestro
 * completo: barra de acciones, tira de contexto, encabezado, líneas y FactBox.
 *
 * Es un componente puramente presentacional: recibe todos los datos y callbacks
 * como props y no contiene lógica de negocio. Para la versión con lógica
 * completa, usar `SmartMasterDocument` dentro de un `MasterDocumentProvider`.
 *
 * @see SmartMasterDocument — versión inteligente con template y adaptador
 * @see MasterDocumentProvider — proveedor de contexto para el modo smart
 *
 * @example
 * ```tsx
 * <MasterDocument
 *   documentId="FA-001"
 *   docTypeBadge="Factura"
 *   docState="draft"
 *   mode="edit"
 *   headerFields={fields}
 *   headerValues={values}
 *   lines={lines}
 *   lineColumns={columns}
 *   onHeaderFieldChange={handleChange}
 *   onLineSave={handleSave}
 *   onDeleteLine={handleDelete}
 * />
 * ```
 */
export function MasterDocument({
  // Identity
  documentId,
  documentNumber,
  docTypeBadge,
  breadcrumbLabel,
  documentTitle,
  companyName,
  onBreadcrumbClick,

  // State
  docState,
  stateBadge,
  transitions,
  onTransition,

  // Mode
  mode,

  // Actions
  actions,

  // Context strip
  contextItems,
  currencyPanel,

  // Header
  headerLayout,
  headerFields,
  headerValues,
  headerErrors,
  onHeaderFieldChange,
  dynamicEntitiesSlot,

  // Lookup/Searcher
  lookupConfigs,
  fetcher,
  onRecordSelect,

  // Lines
  linesTitle,
  lines,
  lineColumns,
  lineFields,
  lineComputedRules,
  lineServerRules,
  onEvaluateServerRule,
  lineWidgetSlot,
  lineDynamicEntitiesSlot,
  onLineFormValuesChange,
  lineModalSections,
  serialSupport,
  serialReadOnly,
  validateSerial,
  fetchAvailableSerials,
  lineAlerts,
  canAddLine = true,
  onAddLine,
  onEditLine,
  onViewLine,
  onDeleteLine,
  onDuplicateLine,
  getLineActions,
  lineEditable = true,
  lineDeletable = true,
  onLineSave,

  // Line tabs
  lineTabs,
  activeLineTabKey,
  onLineTabChange,

  // FactBox
  factBoxSections,

  // Conflict
  conflict,

  // Forbidden / 403
  forbidden,

  // Crossing
  crossing,

  // Loading / Error
  loading,

  // In-flight: deshabilita el guardado de línea mientras hay una mutación en curso
  busy,
  busyLineId,

  // Style
  className,
}: MasterDocumentProps) {
  const { t } = useMasterDocumentTranslation();

  // ── Internal state ────────────────────────────────────────
  const [lineModal, setLineModal] = useState<{
    open: boolean;
    lineId: string | null;
    initialData?: Record<string, unknown>;
    readOnly?: boolean;
  }>({ open: false, lineId: null });
  const [lineErrors, setLineErrors] = useState<FieldErrorMap | undefined>();

  // ── Derived data ──────────────────────────────────────────
  const fieldsBySection = useMemo(
    () => groupFieldsBySection(headerFields),
    [headerFields],
  );

  // Derivado internamente para evaluar `lockedWhen.hasLines` de los campos.
  const hasLines = lines.length > 0;

  const resolvedLinesTitle = linesTitle ?? t('master_document.lines.title');

  // ── Line modal handlers ───────────────────────────────────
  const handleEditLine = useCallback(
    (lineId: string) => {
      // Cuando las líneas no son editables, no abrir el modal de edición.
      if (!lineEditable) return;
      const lineData = lines.find((l) => l.id === lineId);
      setLineModal({
        open: true,
        lineId,
        initialData: lineData ? { ...lineData } : undefined,
      });
      onEditLine(lineId);
    },
    [lineEditable, lines, onEditLine],
  );

  // Abre la línea en modo solo-lectura ("Ver"). Solo se cablea desde
  // LinesSection cuando `lineEditable === false`.
  const handleViewLine = useCallback(
    (lineId: string) => {
      const lineData = lines.find((l) => l.id === lineId);
      setLineModal({
        open: true,
        lineId,
        initialData: lineData ? { ...lineData } : undefined,
        readOnly: true,
      });
      onViewLine?.(lineId);
    },
    [lines, onViewLine],
  );

  const handleAddLine = useCallback(() => {
    setLineModal({ open: true, lineId: null, initialData: undefined });
    onAddLine();
  }, [onAddLine]);

  const handleLineModalConfirm = useCallback(
    async (data: Record<string, unknown>) => {
      const errors = (await onLineSave?.(lineModal.lineId, data)) as FieldErrorMap | undefined;
      if (errors && Object.keys(errors).length > 0) {
        setLineErrors(errors);
      } else {
        setLineErrors(undefined);
        setLineModal({ open: false, lineId: null });
      }
    },
    [lineModal.lineId, onLineSave],
  );

  // Guarda la línea actual y deja el modal abierto para ingresar otra
  const handleLineModalConfirmAndAddAnother = useCallback(
    async (data: Record<string, unknown>): Promise<FieldErrorMap | undefined> => {
      const errors = (await onLineSave?.(null, data)) as FieldErrorMap | undefined;
      if (errors && Object.keys(errors).length > 0) {
        setLineErrors(errors);
        return errors;
      }
      setLineErrors(undefined);
      // El modal permanece abierto con lineId: null → próxima línea nueva
      setLineModal({ open: true, lineId: null, initialData: undefined });
      return undefined;
    },
    [onLineSave],
  );

  const handleLineModalClose = useCallback(() => {
    setLineErrors(undefined);
    setLineModal({ open: false, lineId: null });
  }, []);

  // ── Detail tabs ───────────────────────────────────────────
  // Ayudantes para los contenidos Tipo 2 / Tipo 3. El modal de línea es estado
  // de ESTE componente: el contenedor smart arma el contexto del renderer pero no
  // puede abrir un modal que no controla, así que la correa pasa por aquí.
  //
  // Abre siempre en SOLO LECTURA, y es coherente con el contrato: una pestaña
  // Tipo 2 es de consulta, y las mutaciones van por sus acciones de fila.
  const lineTabHelpers = useMemo<LineTabHelpers>(
    () => ({
      openLineDetail: (lineId: string) => {
        const lineData = lines.find((l) => l.id === lineId);
        if (!lineData) return;
        setLineModal({ open: true, lineId, initialData: { ...lineData }, readOnly: true });
        onViewLine?.(lineId);
      },
    }),
    [lines, onViewLine],
  );

  // Contenido de la pestaña de líneas. Es una función y no un nodo porque solo
  // debe evaluarse cuando esa pestaña está activa: al cambiar de pestaña el
  // contenido se desmonta (ver LineTabsRegion).
  const renderLinesContent = ({ hideTitle }: { hideTitle: boolean }) => (
    <>
      {/* CrossingClassMenu — right-aligned, shown when in selection mode */}
      {crossing?.selectionMode && crossing.classMenuOptions.length > 0 && (
        <div className="flex justify-end">
          <CrossingClassMenu
            options={crossing.classMenuOptions}
            onSelect={crossing.onSelectClass}
            disabled={!crossing.canGenerate}
          />
        </div>
      )}

      {/* StaleDataBanner — rendered above LinesSection when data may be stale */}
      {crossing?.pendingLinesFetchedAt && (
        <StaleDataBanner
          fetchedAt={crossing.pendingLinesFetchedAt}
          onRefresh={crossing.onRefreshPendingLines}
          isRefreshing={crossing.pendingLinesLoading}
        />
      )}

      {crossing?.selectionMode ? (
        /* FE-02: panel de líneas pendientes del origen con cantidad a cruzar */
        <PendingLinesPanel
          lines={crossing.pendingLines}
          selectedLineIds={crossing.selectedLineIds}
          quantities={crossing.quantitiesToCross}
          errors={crossing.lineSelectionErrors}
          loading={crossing.pendingLinesLoading}
          onLineSelect={crossing.onLineSelect}
          onTrySelectTerminalLine={crossing.onTrySelectTerminalLine}
          onQuantityChange={crossing.onQuantityChange}
          formatNumber={crossing.formatNumber}
        />
      ) : (
        <LinesSection
          title={resolvedLinesTitle}
          lines={lines}
          columns={lineColumns}
          lineFields={lineFields}
          alerts={lineAlerts}
          canAddLine={canAddLine && mode !== 'view'}
          onAddLine={handleAddLine}
          onEditLine={handleEditLine}
          onViewLine={handleViewLine}
          onDeleteLine={onDeleteLine}
          onDuplicateLine={onDuplicateLine}
          getLineActions={getLineActions}
          // Hallazgo A de la solicitud: esta línea era la única de las tres que NO
          // cruzaba con el modo. `canAddLine` y `lineDeletable` (arriba y abajo) ya lo
          // hacían, y el comentario de aquéllas enunciaba el criterio —«un documento
          // en sólo lectura no cambia su contenido»— justo antes de aplicárselo sólo a
          // eliminar. El resultado era un lápiz vivo, que disparaba `onEditLine`, en un
          // documento que el propio kit daba por de consulta.
          lineEditable={lineEditable && mode !== 'view'}
          // AC-4: en modo `view` no se borra, igual que no se agrega. Es el mismo
          // criterio que ya gobierna `canAddLine` justo arriba: un documento en sólo
          // lectura no cambia su contenido, y ofrecer el botón sería prometer una
          // escritura que la pantalla no admite.
          lineDeletable={lineDeletable && mode !== 'view'}
          lineStatuses={crossing?.lineStatuses}
          onOpenLineCloseModal={crossing?.onOpenLineCloseModal}
          lineActions={crossing?.lineActions}
          busyLineId={busyLineId}
          hideTitle={hideTitle}
        />
      )}
    </>
  );

  // Pestañas efectivas = las declaradas + la de trazabilidad si hay cruce. Sin
  // ninguna de las dos cosas la lista queda vacía y el detalle se pinta directo,
  // sin barra ni panel: es el camino de siempre y no debe cambiar de DOM.
  const detailTabs = useMemo<LineTabConfig[]>(() => {
    const declared = lineTabs ?? [];
    if (declared.length === 0 && !crossing) return [];

    const result: LineTabConfig[] =
      declared.length > 0
        ? [...declared]
        : // Con cruce pero sin pestañas declaradas, la de líneas es implícita:
          // es la que ya existía en la tira quemada.
          [{ key: 'lines', label: t('master_document.lines.tab'), type: 'lines' }];

    if (crossing) {
      // Alias local: el estrechamiento de un parámetro no sobrevive dentro del
      // closure de `render`, y sin él TS lo trata como posiblemente indefinido.
      const cx = crossing;
      result.push({
        key: TRACEABILITY_TAB_KEY,
        label: cx.traceabilityLabel || t('master_document.document.traceability'),
        type: 'render',
        render: () => (
          <DocumentTraceabilityTree
            tree={cx.traceabilityTree}
            onNodeClick={cx.onTraceabilityNodeClick}
            loading={cx.traceabilityLoading}
            error={cx.traceabilityError}
            onRetry={cx.onRetryTraceability}
          />
        ),
      });
    }

    return result;
  }, [lineTabs, crossing, t]);

  // Pestaña de contenido seleccionada, INDEPENDIENTE de la trazabilidad: así,
  // al volver del árbol se restaura la que el usuario estaba viendo.
  const [internalTabKey, setInternalTabKey] = useState<string | null>(null);
  const firstContentTabKey =
    detailTabs.find((tab) => tab.key !== TRACEABILITY_TAB_KEY)?.key ?? TRACEABILITY_TAB_KEY;
  const contentTabKey = activeLineTabKey ?? internalTabKey ?? firstContentTabKey;
  const activeDetailTabKey =
    crossing?.activeTab === 'traceability' ? TRACEABILITY_TAB_KEY : contentTabKey;

  const handleDetailTabChange = useCallback(
    (key: string) => {
      if (key === TRACEABILITY_TAB_KEY) {
        crossing?.onTabChange('traceability');
        return;
      }
      // Volver de la trazabilidad a cualquier pestaña de contenido devuelve el
      // estado del cruce a 'lines': es lo que espera el resto de su máquina.
      if (crossing?.activeTab === 'traceability') crossing.onTabChange('lines');
      setInternalTabKey(key);
      onLineTabChange?.(key);
    },
    [crossing, onLineTabChange],
  );

  // ── Loading state ─────────────────────────────────────────
  if (loading) {
    return <SkeletonDocument className={className} />;
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div className={clsx('w-full h-full flex flex-col', className)}>
      {/* Cabecera del detalle, al canon de MasterCrud: apilada y SIN caja propia.
          Ya no es `sticky`: la barra de 52px se quedaba pegada arriba porque era
          la única referencia del documento, pero una cabecera de tres alturas
          fijada roba un tercio del alto útil en portátiles. La identidad del
          documento —tipo, número, centro de operación— sigue a la vista en la
          franja que abre la tarjeta. */}
      <div className="shrink-0">
        <ActionBar
          breadcrumbLabel={breadcrumbLabel}
          documentTitle={documentTitle}
          companyName={companyName}
          onBreadcrumbClick={onBreadcrumbClick}
          documentId={documentId}
          documentNumber={documentNumber}
          stateBadge={stateBadge}
          docTypeBadge={docTypeBadge}
          transitions={transitions}
          onTransition={onTransition}
          actions={actions}
        />
      </div>

      {/* Main content area with optional FactBox */}
      <ContentLayout
        factBox={
          factBoxSections ? (
            <FactBox sections={factBoxSections} />
          ) : undefined
        }
      >
        {/* Envoltorio + tarjeta del canon (ADR-027, detalle de MasterCrud): el aire
            lo pone el envoltorio y la ÚNICA superficie es la tarjeta blanca con
            `rounded-2xl`, borde y `shadow-sm`. Antes el contenido colgaba
            directamente del panel gris, sin tarjeta y con un `px-5` que no
            cuadraba con el `px-4 md:px-6` de la cabecera: por eso el título y
            los campos no compartían eje vertical. */}
        <div className="w-full px-4 md:px-6 pb-4 flex-1 min-h-0 flex flex-col">
        <div className="w-full bg-white dark:bg-dark-bg-primary rounded-2xl border border-border-primary dark:border-dark-border-primary overflow-hidden flex flex-col flex-1 min-h-0 shadow-sm">
        <ContextStrip items={contextItems} currencyPanel={currencyPanel} />
        {/* Dentro de la tarjeta ya no hay tarjetas: encabezado, campos dinámicos
            y detalle son SECCIONES de una sola superficie. Para que se distingan
            sin volver a encajonarlas, cada bloque que abre una región nueva se
            separa con una regla a TODO el ancho de la tarjeta —de ahí el
            `-mx` que anula el gutter y lo repone—, y el aire entre bloques sube
            a 20px. Es lo que deja legibles dos barras de pestañas en la misma
            tarjeta: la regla dice dónde empieza cada región y el subrayado
            deslizante de cada barra dice cuál es su pestaña activa. */}
        {/* EL SCROLL VIVE AQUÍ, no en el panel de `ContentLayout`. Así la franja de
            identidad del documento —tipo, número, centro de operación— y el borde
            superior de la tarjeta se quedan quietos, y sólo se desplaza el contenido de
            las pestañas. Con el scroll en el panel, al bajar se iba la ficha entera y el
            usuario perdía de vista qué documento estaba mirando. */}
        <div className="flex flex-col gap-5 px-4 md:px-6 pt-4 pb-8 flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          {/* Forbidden / 403 banner */}
          {forbidden && (
            <div
              role="alert"
              className="flex items-start justify-between gap-3 px-4 py-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
            >
              <span className="text-sm text-red-700 dark:text-red-400">{forbidden.message}</span>
              {forbidden.onDismiss && (
                <button
                  type="button"
                  onClick={forbidden.onDismiss}
                  aria-label={t('master_document.chrome.closeNotice')}
                  className={clsx(inlineActionButtonDanger, 'shrink-0')}
                >
                  {t('master_document.chrome.close')}
                </button>
              )}
            </div>
          )}

          {/* Header Renderer */}
          <HeaderRenderer
            mode={headerLayout.mode}
            columns={headerLayout.columns}
            sections={headerLayout.sections}
            fieldsBySection={fieldsBySection}
            values={headerValues}
            errors={headerErrors}
            onFieldChange={onHeaderFieldChange}
            docState={docState}
            mode_form={mode}
            hasLines={hasLines}
            lookupConfigs={lookupConfigs}
            fetcher={fetcher}
            onRecordSelect={onRecordSelect}
          />

          {/* Crossing source selector — rendered below header when crossing is active */}
          {crossing?.sourceSelector && (
            <CrossingSourceSelector
              selectedDocuments={crossing.sourceSelector.selectedDocuments}
              onAdd={crossing.sourceSelector.onAdd}
              onRemove={crossing.sourceSelector.onRemove}
              locked={crossing.sourceSelector.locked}
              searchDocuments={crossing.sourceSelector.searchDocuments}
            />
          )}

          {/* Generate error banner — shown when crossing generate fails */}
          {crossing?.generateError && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <span className="text-sm text-red-700 dark:text-red-400">{crossing.generateError}</span>
              <button
                type="button"
                onClick={crossing.onClearGenerateError}
                className={clsx(inlineActionButtonDanger, 'shrink-0')}
              >
                {t('master_document.chrome.close')}
              </button>
            </div>
          )}

          {/* Page-level business rule errors (BR-11, BR-13, BR-15, BR-16) */}
          {crossing?.pageErrors?.map((msg, idx) => (
            <div
              key={idx}
              className="flex items-start justify-between gap-3 px-4 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700"
            >
              <span className="text-sm text-amber-800 dark:text-amber-300">{msg}</span>
              {crossing.onDismissPageError && (
                <button
                  type="button"
                  onClick={() => crossing.onDismissPageError!(idx)}
                  aria-label={t('master_document.chrome.closeNotice')}
                  className={clsx(inlineActionButtonNeutral, 'shrink-0')}
                >
                  {t('master_document.chrome.close')}
                </button>
              )}
            </div>
          ))}

          {/* Campos adicionales (EAV) del encabezado. Van después de los avisos
              —que son transitorios y pertenecen al encabezado— y justo encima del
              detalle, que es donde el usuario los busca: son datos del documento,
              no de sus líneas. */}
          {dynamicEntitiesSlot && (
            <div className="-mx-4 px-4 pt-5 border-t border-border-secondary dark:border-dark-border-primary">
              {dynamicEntitiesSlot}
            </div>
          )}

          {/* Área de detalle: sin pestañas declaradas y sin cruce, es la grilla y
              nada más — mismo DOM que antes de existir esta capacidad. */}
          {/* ⚠️ `-mx-4 px-4` con UN SOLO valor de escala y sin variante `md:`, a
              propósito. Con `-mx-4 md:-mx-6 px-4 md:px-6` el margen y el padding NO se
              cancelaban: medido en dev el 2026-09-09 a 1470px, el computado daba
              `margin-left: -24px` (ganaba `md:-mx-6`) y `padding-left: 16px` (ganaba
              `px-4` a `md:px-6`, por orden de cascada en el CSS del kit). Resultado: la
              barra de pestañas de líneas quedaba 8px a la izquierda de la del
              encabezado. Con un valor único siempre se anulan. */}
          <div className="-mx-4 px-4 pt-5 border-t border-border-secondary dark:border-dark-border-primary flex flex-col gap-5">
          {detailTabs.length > 0 ? (
            <LineTabsRegion
              tabs={detailTabs}
              activeKey={activeDetailTabKey}
              onChange={handleDetailTabChange}
              renderLines={renderLinesContent}
              helpers={lineTabHelpers}
            />
          ) : (
            renderLinesContent({ hideTitle: false })
          )}

          {/* Los widgets, por debajo de `xl`, como última sección del documento y
              dentro de SU MISMO scroll. Aquí y no en `ContentLayout` porque aquello
              es una fila flex: puesto ahí, el bloque saldría al lado del documento
              en vez de debajo. Por encima de `xl` no pinta nada — manda el riel. */}
          {factBoxSections && <FactBoxInline sections={factBoxSections} />}
          </div>
        </div>
        </div>
        </div>
      </ContentLayout>

      {/* Line Form Modal */}
      {onLineSave && (
        <LineFormModal
          open={lineModal.open}
          onClose={handleLineModalClose}
          onConfirm={handleLineModalConfirm}
          onConfirmAndAddAnother={lineModal.lineId === null ? handleLineModalConfirmAndAddAnother : undefined}
          fields={lineFields}
          initialData={lineModal.initialData}
          docState={docState}
          computedRules={lineComputedRules}
          serverRules={lineServerRules}
          onEvaluateServerRule={onEvaluateServerRule}
          sections={lineModalSections}
          widgetSlot={lineWidgetSlot}
          dynamicEntitiesSlot={
            // Solo con el modal abierto: cerrado, `LineFormModal` devuelve null y
            // construir el bloque sería montar formularios que nadie ve.
            lineModal.open && lineDynamicEntitiesSlot
              ? lineDynamicEntitiesSlot({ lineId: lineModal.lineId })
              : undefined
          }
          onFormValuesChange={onLineFormValuesChange}
          lookupConfigs={lookupConfigs}
          fetcher={fetcher}
          headerValues={headerValues}
          serialSupport={serialSupport}
          serialReadOnly={serialReadOnly}
          validateSerial={validateSerial}
          fetchAvailableSerials={fetchAvailableSerials}
          errors={lineErrors}
          busy={busy}
          readOnly={lineModal.readOnly}
          title={lineModal.readOnly ? t('master_document.lines.viewLine') : lineModal.lineId ? t('master_document.lines.editLine') : t('master_document.lines.newLine')}
          confirmLabel={lineModal.lineId ? t('master_document.lines.saveChanges') : t('master_document.lines.addLine')}
          cancelLabel={lineModal.readOnly ? t('master_document.chrome.close') : undefined}
          columns={2}
        />
      )}

      {/* Conflict Modal */}
      {conflict && (
        <ConflictModal
          open
          modifiedBy={conflict.modifiedBy}
          modifiedAgo={conflict.modifiedAgo}
          onReload={conflict.onReload}
          onKeepEditing={conflict.onKeepEditing}
          dismissable={conflict.dismissable}
          message={conflict.message}
        />
      )}

      {/* Line Close Modal — always rendered, controlled by open prop */}
      {crossing && (
        <LineCloseModal
          open={crossing.lineCloseModal.open}
          onClose={crossing.onCloseLineCloseModal}
          onConfirm={crossing.onConfirmLineClose}
          mode={crossing.lineCloseModal.mode}
          lineDescription={crossing.lineCloseModal.lineDescription}
          isLoading={crossing.lineCloseModal.isLoading}
          error={crossing.lineCloseModal.error}
        />
      )}

      {/* Crossing Generate Modal — always rendered, controlled by open prop */}
      {crossing && (
        <CrossingGenerateModal
          open={crossing.generateModal.open}
          classLabel={crossing.generateModal.classLabel}
          fields={crossing.generateHeaderFields ?? []}
          onConfirm={crossing.onConfirmGenerate}
          onCancel={crossing.onCancelGenerateModal}
          isLoading={crossing.generateModal.isLoading}
          error={crossing.generateModal.error}
        />
      )}
    </div>
  );
}
